import { observer } from 'mobx-react-lite';
import React from 'react';
import { Header as RawHeader } from '../../../components/landingPage/Header';
import {
    SanityObject,
    useSanity,
    MenuItem,
    renderMenuItemHrefs,
    isMenuLink,
    RenderedMenuLink,
} from '../../../lib/landingPage';
import { Image, ImageProps } from '../Image';
import { ButtonLink, ButtonLinkProps } from '../Link';
import { Icon } from '../Icon';
import { Await } from '../../../components/Await';
import { addQueryParamsToUrl } from '../../../lib';

export type HeaderProps = {
    children: SanityObject<MenuItem>[];
    logo: SanityObject<ImageProps>;
    signupButton: SanityObject<ButtonLinkProps>;
    loginButton: SanityObject<ButtonLinkProps>;
};

type HeaderResolvedProps = {
    header: SanityObject<HeaderProps>;
    showSignupButton?: boolean;
    hideMenu?: boolean;
    hideButtons?: boolean;
    disableLogoLink?: boolean;
    loginUtmParam?: Record<string, string>;
    signUpUtmParam?: Record<string, string>;
};

const menuLinkToHeaderNode = (menuLink: RenderedMenuLink) => {
    return {
        title: menuLink.title,
        description: menuLink.description,
        icon: menuLink.icon.slug && <Icon value={menuLink.icon} />,
        href: menuLink.link,
    };
};

const HeaderResolved = observer(
    ({
        header,
        showSignupButton,
        loginUtmParam,
        signUpUtmParam,
        hideMenu,
        hideButtons,
        disableLogoLink,
    }: HeaderResolvedProps) => {
        const renderHeaderItems = renderMenuItemHrefs(header.children).map(
            child => {
                if (isMenuLink(child)) {
                    return {
                        text: child.title,
                        href: child.link,
                    };
                }
                const validChildren = child.children.filter(
                    isMenuLink
                ) as RenderedMenuLink[];
                return {
                    text: child.text,
                    subItems: validChildren.map(menuLinkToHeaderNode),
                };
            }
        );
        return (
            <RawHeader
                items={renderHeaderItems}
                loginButton={
                    <ButtonLink
                        value={{
                            ...header.loginButton,
                            href: !loginUtmParam
                                ? header.loginButton.href
                                : addQueryParamsToUrl(
                                      header.loginButton.href,
                                      loginUtmParam
                                  ),
                        }}
                    />
                }
                hideMenu={hideMenu}
                hideButtons={hideButtons}
                disableLogoLink={disableLogoLink}
                signupButton={
                    <ButtonLink
                        value={{
                            ...header.signupButton,
                            href: !signUpUtmParam
                                ? header.signupButton.href
                                : addQueryParamsToUrl(
                                      header.signupButton.href,
                                      signUpUtmParam
                                  ),
                        }}
                    />
                }
                logo={<Image value={header.logo} />}
                showSignupButton={showSignupButton}
            />
        );
    }
);

export const Header = observer(
    ({
        showSignupButton = true,
        loginUtmParam,
        signUpUtmParam,
        hideMenu,
        hideButtons,
        disableLogoLink,
    }: {
        showSignupButton?: boolean;
        hideMenu?: boolean;
        loginUtmParam?: Record<string, string>;
        signUpUtmParam?: Record<string, string>;
        hideButtons?: boolean;
        disableLogoLink?: boolean;
    }) => {
        const store = useSanity();
        return (
            <Await
                views={{ header: store.asyncHeader }}
                resolved={HeaderResolved}
                extra={{
                    showSignupButton,
                    loginUtmParam,
                    signUpUtmParam,
                    hideMenu,
                    hideButtons,
                    disableLogoLink,
                }}
            />
        );
    }
);
